import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ReviewCountResponseSchema,
  ReviewListResponseSchema,
  ReviewSchema,
  type CreateReviewInput,
  type PatchReviewInput,
  type Review,
  type ReviewCountResponse,
  type ReviewPlatform,
} from "@/api/types/review";

/** Query-key factory — mirrors the invoices hook's convention (cursor list + count). */
export const reviewKeys = {
  all: ["reviews"] as const,
  lists: () => [...reviewKeys.all, "list"] as const,
  list: (params: { platform?: ReviewPlatform; event_id?: string; limit?: number }) =>
    [...reviewKeys.lists(), params] as const,
  detail: (id: string | undefined) => [...reviewKeys.all, "detail", id] as const,
  count: () => [...reviewKeys.all, "count"] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
}

interface UseReviewsParams {
  platform?: ReviewPlatform;
  event_id?: string;
  limit?: number;
}

/** List query with cursor pagination, mirroring useInvoices'/useEvents' convention. */
export function useReviews(params: UseReviewsParams = {}) {
  const { platform, event_id, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: reviewKeys.list({ platform, event_id, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (platform) searchParams.set("platform", platform);
      if (event_id) searchParams.set("event_id", event_id);
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/reviews?${searchParams.toString()}`);
      return validateResponse(ReviewListResponseSchema, data, "GET /v2/reviews");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15 * 1000,
  });
}

/**
 * The "N of 20" counter — its own endpoint, polled independently of the list
 * so the dashboard/ReviewsPage header can render without pulling every row.
 */
export function useReviewCount() {
  return useQuery<ReviewCountResponse>({
    queryKey: reviewKeys.count(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/reviews/count");
      return validateResponse(ReviewCountResponseSchema, data, "GET /v2/reviews/count");
    },
    staleTime: 10 * 1000,
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();
  return useMutation<Review, unknown, CreateReviewInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/reviews", input);
      return validateResponse(ReviewSchema, data, "POST /v2/reviews");
    },
    onSuccess: (review) => {
      queryClient.setQueryData(reviewKeys.detail(review.id), review);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchReview() {
  const queryClient = useQueryClient();
  return useMutation<Review, unknown, PatchReviewInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/reviews/${id}`, patch);
      return validateResponse(ReviewSchema, data, "PATCH /v2/reviews/:id");
    },
    onSuccess: (review) => {
      queryClient.setQueryData(reviewKeys.detail(review.id), review);
      invalidateAll(queryClient);
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/reviews/${id}`);
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}
